import { Typography, Box, Stack, Chip, Divider } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock, faLocationDot } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { DateTimeFactory } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { DeliveryTypeFilter, GroupBy } from "../../meetingServiceTypes"
import SingleOrder from "./SingleOrder"
import { GroupedByLocation, GroupedByTime, getTimeSlotLabel } from "./helpers"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"

const useStyles = makeStyles()((theme) => ({
    iconHeaderWrap: {
        display: "flex",
        alignItems: "center",
        gap: ".625rem",
    },
    groupHeader: {
        margin: "0 3.5rem",
        padding: ".25rem 0",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[900]}`,
        "@media print": {
            margin: 0,
        },
    },
    dateHeader: {
        color: "white",
        background: "rgba(0, 0, 0, 0.87)",
        margin: "0 3.5rem",
        padding: "0 .5rem",
        "@media print": {
            margin: 0,
        },
    },
    ordersContainer: {
        padding: "0 3.5rem",
        "@media print": {
            padding: 0,
        },
    },
    orderDivider: {
        borderStyle: "dashed",
        margin: "0 3.5rem",
        borderColor: theme.palette.divider,
    },
}))

type GroupHeaderProps = Readonly<{
    groupKey: string
    groupBy: GroupBy
    dateTimeFactory: DateTimeFactory
}>

const isDeliveryTypeEnumKey = (key: string): boolean => {
    return Object.values(DeliveryTypeFilter).includes(key as DeliveryTypeFilter)
}

function GroupHeader({ groupKey, groupBy, dateTimeFactory }: GroupHeaderProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    const getLocationLabel = (key: string): string => {
        if (isDeliveryTypeEnumKey(key)) {
            return l10n.getStringForEnum("delivery-type-filter", key, key)
        }
        return key
    }

    return (
        <Box className={cx(classes.iconHeaderWrap, classes.groupHeader)}>
            <FontAwesomeIcon icon={groupBy === "location" ? faLocationDot : faClock} size="lg" />
            <Typography variant="h5" component="h2" fontWeight={500}>
                {groupBy === "time" ? getTimeSlotLabel(groupKey, dateTimeFactory) : getLocationLabel(groupKey)}
            </Typography>
        </Box>
    )
}

type DateChipProps = Readonly<{
    dateKey: string
    dateTimeFactory: DateTimeFactory
}>

function DateChip({ dateKey, dateTimeFactory }: DateChipProps) {
    const { classes } = useStyles()

    const formattedDate = dateTimeFactory.formatDate(dateTimeFactory.fromISO(dateKey))

    return (
        <Box>
            <Chip
                label={
                    <Typography variant="h6" fontWeight={500}>
                        {formattedDate}
                    </Typography>
                }
                className={classes.dateHeader}
            />
        </Box>
    )
}

type TimeGroupsSectionProps = Readonly<{
    timeGroups: Record<string, MeetingDetailsOrder[]>
    dateTimeFactory: DateTimeFactory
    groupBy: GroupBy
}>

function TimeGroupsSection({ timeGroups, dateTimeFactory, groupBy }: TimeGroupsSectionProps) {
    const { classes } = useStyles()

    const isGroupByTime = groupBy === "time"

    const entries = Object.entries(timeGroups).sort(
        ([slotA], [slotB]) => dateTimeFactory.fromISO(slotA).toMillis() - dateTimeFactory.fromISO(slotB).toMillis(),
    )

    const groupsCount = entries.length

    return (
        <Stack gap="3rem">
            {entries.map(([groupKey, orders], index) => (
                <Stack gap="2rem" key={groupKey}>
                    {isGroupByTime && (
                        <GroupHeader groupKey={groupKey} groupBy={groupBy} dateTimeFactory={dateTimeFactory} />
                    )}

                    <Stack gap="2.25rem" className={classes.ordersContainer}>
                        {orders.map((order, index) => (
                            <SingleOrder key={order.id} order={order} itemIndex={index} totalOrders={orders.length} />
                        ))}
                    </Stack>
                    {!isGroupByTime && index < groupsCount - 1 && <Divider className={classes.orderDivider} />}
                </Stack>
            ))}
        </Stack>
    )
}

type DeliveryGroupsProps = Readonly<{
    groupBy: GroupBy
    grouped: GroupedByTime | GroupedByLocation
    dateTimeFactory: DateTimeFactory
}>

export function DeliveryGroups({ groupBy, grouped, dateTimeFactory }: DeliveryGroupsProps) {
    const { classes } = useStyles()

    return (
        <Stack gap="4rem">
            {groupBy === "time" &&
                Object.entries(grouped as GroupedByTime)
                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                    .map(([dateKey, timeGroups]) => (
                        <Stack gap="1rem" key={dateKey}>
                            <DateChip dateKey={dateKey} dateTimeFactory={dateTimeFactory} />

                            <TimeGroupsSection
                                timeGroups={timeGroups}
                                dateTimeFactory={dateTimeFactory}
                                groupBy={groupBy}
                            />
                        </Stack>
                    ))}

            {groupBy === "location" &&
                Object.entries(grouped as GroupedByLocation)
                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                    .map(([dateKey, locationsMap]) => (
                        <Stack gap="1rem" key={dateKey}>
                            <DateChip dateKey={dateKey} dateTimeFactory={dateTimeFactory} />

                            {Object.entries(locationsMap).map(([locationName, timeGroups]) => (
                                <Stack gap="2rem" key={locationName}>
                                    <GroupHeader
                                        groupKey={locationName}
                                        groupBy={groupBy}
                                        dateTimeFactory={dateTimeFactory}
                                    />

                                    <TimeGroupsSection
                                        timeGroups={timeGroups}
                                        dateTimeFactory={dateTimeFactory}
                                        groupBy={groupBy}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    ))}
        </Stack>
    )
}
